import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import DefaultApiLimit from '../../../../config/defaultLimit';
import { EMSPPreauthInterface } from '../../../../interface/emsp-preauth.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  disableConsumerPaymentHistoryFilterApplied,
  fetchConsumerPaymentHistory,
  fetchConsumerPaymentHistoryFilters,
  refreshConsumerPaymentHistory,
  setSortData,
  updateConsumerPaymentHistoryFilters,
} from '../../../../redux/slices/EndConsumerPaymentHistory';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import EmspSessionSidePanel from '../../../../shared-components/table-side-panel/EmspSessionSidePanel';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import { getDaysBetween } from '../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatUtcString from '../../../../shared-functions/FormatUtcString';
import { permissionCheck } from '../../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import emspPreauthApi from '../../../../utils/api/v2/emsp-preauth';

/**
 * Format ISO date string into standard date and time string.
 * @param {string | null | undefined} isoString - The ISO date string.
 * @returns {string} The formatted date and time string or hyphen if null.
 */
const formatDateTime = (isoString: string | null | undefined): string => {
  if (!isoString) return '-';
  const dateString = ConvertUTCtoDate(isoString);
  const timeString = FormatUtcString(ConvertUTCtoHHMM(isoString));
  return `${dateString} ${timeString}`;
};

/**
 * Helper to render status badge with appropriate styling class.
 * @param {string | null | undefined} status - Status string.
 * @returns {React.JSX.Element} The status badge element.
 */
const renderStatusBadge = (status: string | null | undefined): React.JSX.Element => {
  if (!status) return <span>-</span>;
  const lowerStatus = status.toLowerCase();
  return <span className={`preauth-status-badge ${lowerStatus}`}>{status}</span>;
};

/**
 * PaymentHistoryList component rendering EMSP preauth payment history table for end consumer profile tab.
 * @returns {React.JSX.Element} The PaymentHistoryList component.
 */
function PaymentHistoryList(): React.JSX.Element {
  const { t } = useTranslation('transaction');
  const dispatch = useAppDispatch();
  const { selectedEndConsumer } = useAppSelector((state) => state.endConsumerType);

  const {
    preauths,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.ConsumerPaymentHistory);

  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [statusCounts, setStatusCounts] = useState<{
    counts?: {
      RESERVED?: number;
      LINKED?: number;
      CAPTURED?: number;
      RELEASED?: number;
      ORPHANED?: number;
    };
    total?: number;
  }>();

  /**
   * Fetch status counts for EMSP preauth payments.
   */
  const fetchStatusCounts = async (): Promise<void> => {
    try {
      const queryParameters = GetParametersFromCommonFilters(filters);
      if (selectedEndConsumer?.userCustomId) {
        queryParameters.userCustomId = selectedEndConsumer.userCustomId;
      }
      const response = await emspPreauthApi.fetchPreauthStatusCounts(queryParameters);
      setStatusCounts(response?.data);
    } catch (error) {
      console.error('Failed to fetch preauth status counts', error);
    }
  };

  const activeStatusFilter = filters.find((f) => f.key === 'status');

  /**
   * Check if a status badge card is currently active in filters.
   * @param {string} statusKey - The status key to check.
   * @returns {boolean} True if the status is active in filters.
   */
  const isStatusCardActive = (statusKey: string): boolean => {
    if (!activeStatusFilter || !activeStatusFilter.applyFilter) {
      return false;
    }
    if (Array.isArray(activeStatusFilter.value)) {
      return activeStatusFilter.value.includes(statusKey);
    }
    return activeStatusFilter.value === statusKey;
  };

  /**
   * Handle click event on a status badge card to toggle filter.
   * @param {string} statusKey - The status key to filter by.
   */
  const handleStatusCardClick = (statusKey: string): void => {
    const isActive = isStatusCardActive(statusKey);
    if (isActive) {
      dispatch(
        updateConsumerPaymentHistoryFilters({
          key: 'status',
          type: 'remove',
          value: '',
        }),
      );
    } else {
      dispatch(
        updateConsumerPaymentHistoryFilters({
          key: 'status',
          type: 'add',
          value: statusKey,
        }),
      );
    }
  };

  /**
   * Handle click on the Total stat item to clear status filter.
   */
  const handleTotalCardClick = (): void => {
    if (activeStatusFilter?.applyFilter) {
      dispatch(
        updateConsumerPaymentHistoryFilters({
          key: 'status',
          type: 'remove',
          value: '',
        }),
      );
    }
  };

  /**
   * Fetch EMSP preauth list via Redux action.
   * @param {any} data - Query payload for fetching preauths.
   */
  const fetchPreauths = (data: any) => {
    if (getDaysBetween(data.startDate, data.endDate) > 30) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading && selectedEndConsumer?.userCustomId) {
      dispatch(
        fetchConsumerPaymentHistory({
          userCustomId: selectedEndConsumer.userCustomId,
          ...data,
        }),
      );
    }
  };

  /**
   * Refresh preauth table data using current filters and sorting parameters.
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {};
    fetchPreauths({ ...queryParameters, page, limit, ...sortValue });
  };

  const { handleSidePanelClose, renderSidePanelTopLeftContent } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: preauths,
    disableEdit: true,
    disableDelete: true,
  });

  /**
   * Auto-refresh handler for live table refresh.
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (selectedEndConsumer?.userCustomId) {
      dispatch(
        refreshConsumerPaymentHistory({
          userCustomId: selectedEndConsumer.userCustomId,
          ...queryParameters,
          page,
          limit,
          sortBy,
          sortField,
        }),
      );
    }
  };

  /**
   * Column configuration for CustomTable.
   */
  const column: any = [
    {
      label: t('referenceId', { defaultValue: 'Reference Id' }),
      accessorKey: 'internalRef',
      sortAllow: true,
    },
    {
      label: t('pspPreauthId', { defaultValue: 'PSP Preauth ID' }),
      accessorKey: 'pspPreauthId',
      sortAllow: true,
    },
    {
      label: t('paymentMethod', { defaultValue: 'Payment Method' }),
      accessorKey: 'paymentMethod',
      sortAllow: true,
    },
    {
      label: t('tokenUid', { defaultValue: 'Token UID' }),
      accessorKey: 'tokenUid',
      sortAllow: true,
    },
    {
      label: t('amount', { defaultValue: 'Amount' }),
      accessorKey: 'amount',
      sortAllow: true,
      /**
       * Custom formatter for Amount.
       * @param {EMSPPreauthInterface} row - Row data.
       * @returns {React.JSX.Element} The formatted amount element.
       */
      customData: (row: EMSPPreauthInterface) => (
        <span>
          {ToFixedValue(row.amount)} {row.currency || 'SAR'}
        </span>
      ),
    },
    {
      label: t('capturedAmount', { defaultValue: 'Captured Amount' }),
      accessorKey: 'capturedAmount',
      sortAllow: true,
      /**
       * Custom formatter for Captured Amount.
       * @param {EMSPPreauthInterface} row - Row data.
       * @returns {React.JSX.Element} The formatted captured amount element.
       */
      customData: (row: EMSPPreauthInterface) => (
        <span>
          {row.capturedAmount != null
            ? `${ToFixedValue(row.capturedAmount)} ${row.currency || 'SAR'}`
            : '-'}
        </span>
      ),
    },
    {
      label: t('status', { defaultValue: 'Status' }),
      accessorKey: 'status',
      sortAllow: true,
      /**
       * Custom formatter for Status.
       * @param {EMSPPreauthInterface} row - Row data.
       * @returns {React.JSX.Element} The status badge element.
       */
      customData: (row: EMSPPreauthInterface) => renderStatusBadge(row.status),
    },
    {
      label: t('sessionId', { defaultValue: 'Session ID' }),
      accessorKey: 'cpoSessionId',
      queryParamsKey: 'sessionId',
      queryParamsValue: 'cpoSessionId',
      sidePanelComponent: <EmspSessionSidePanel fetchIdFrom="queryParams" />,
      sortAllow: false,
    },
    {
      label: t('locationId', { defaultValue: 'Location ID' }),
      accessorKey: 'locationId',
      sortAllow: false,
    },
    {
      label: t('evseUid', { defaultValue: 'EVSE UID' }),
      accessorKey: 'evseUid',
      sortAllow: false,
    },
    {
      label: t('connectorId', { defaultValue: 'Connector ID' }),
      accessorKey: 'connectorId',
      sortAllow: false,
    },
    {
      label: t('gatewayTransactionId', { defaultValue: 'Gateway Tx ID' }),
      accessorKey: 'gatewayTransactionId',
      sortAllow: false,
    },
    {
      label: t('linkedAt', { defaultValue: 'Linked At' }),
      accessorKey: 'linkedAt',
      sortAllow: true,
      /**
       * Custom formatter for Linked At date.
       * @param {EMSPPreauthInterface} row - Row data.
       * @returns {React.JSX.Element} The formatted date element.
       */
      customData: (row: EMSPPreauthInterface) => <span>{formatDateTime(row.linkedAt)}</span>,
    },
    {
      label: t('capturedAt', { defaultValue: 'Captured At' }),
      accessorKey: 'capturedAt',
      sortAllow: true,
      /**
       * Custom formatter for Captured At date.
       * @param {EMSPPreauthInterface} row - Row data.
       * @returns {React.JSX.Element} The formatted date element.
       */
      customData: (row: EMSPPreauthInterface) => <span>{formatDateTime(row.capturedAt)}</span>,
    },
    {
      label: t('expiresAt', { defaultValue: 'Expires At' }),
      accessorKey: 'expiresAt',
      sortAllow: true,
      /**
       * Custom formatter for Expires At date.
       * @param {EMSPPreauthInterface} row - Row data.
       * @returns {React.JSX.Element} The formatted date element.
       */
      customData: (row: EMSPPreauthInterface) => <span>{formatDateTime(row.expiresAt)}</span>,
    },
  ];

  /**
   * Current table columns definitions for export mapping.
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('internalRef', { defaultValue: 'Internal Ref' }), accessorKey: 'internalRef' },
    { label: t('pspPreauthId', { defaultValue: 'PSP Preauth ID' }), accessorKey: 'pspPreauthId' },
    { label: t('paymentMethod', { defaultValue: 'Payment Method' }), accessorKey: 'paymentMethod' },
    { label: t('tokenUid', { defaultValue: 'Token UID' }), accessorKey: 'tokenUid' },
    { label: t('amount', { defaultValue: 'Amount' }), accessorKey: 'amount' },
    {
      label: t('capturedAmount', { defaultValue: 'Captured Amount' }),
      accessorKey: 'capturedAmount',
    },
    { label: t('status', { defaultValue: 'Status' }), accessorKey: 'status' },
    { label: t('sessionId', { defaultValue: 'Session ID' }), accessorKey: 'cpoSessionId' },
    { label: t('locationId', { defaultValue: 'Location ID' }), accessorKey: 'locationId' },
    { label: t('evseUid', { defaultValue: 'EVSE UID' }), accessorKey: 'evseUid' },
    { label: t('connectorId', { defaultValue: 'Connector ID' }), accessorKey: 'connectorId' },
    {
      label: t('gatewayTransactionId', { defaultValue: 'Gateway Tx ID' }),
      accessorKey: 'gatewayTransactionId',
    },
    { label: t('createdAt', { defaultValue: 'Created At' }), accessorKey: 'createdAt' },
  ];

  /**
   * Handle pagination page or limit change.
   * @param {any} value - The pagination value containing page and limit.
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {};
    fetchPreauths({ ...value, ...queryParameters, ...sortValue });
  };

  /**
   * Active export columns filtering out hidden columns.
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export table data to PDF or CSV format.
   * @param {string} type - File type ('csv' or 'pdf').
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = {
      ...queryParameters,
      limit: 10000,
    };
    if (selectedEndConsumer?.userCustomId) {
      payload.userCustomId = selectedEndConsumer.userCustomId;
    }
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;

    const apiCall = emspPreauthApi.fetchPreauthList(payload);
    apiCall
      .then((response) => {
        const rawResults = response?.data?.results || [];
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = rawResults.map((row: EMSPPreauthInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'amount':
                return `${ToFixedValue(row.amount)} ${row.currency || 'SAR'}`;
              case 'capturedAmount':
                return row.capturedAmount != null
                  ? `${ToFixedValue(row.capturedAmount)} ${row.currency || 'SAR'}`
                  : '-';
              case 'createdAt':
                return formatDateTime(row.createdAt);
              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'emsp-preauth-payments.csv');
          } else {
            exportPDF(headers, rows, 'emsp-preauth-payments.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || 'Export failed',
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle table header column sort change.
   * @param {any} sortData - Sort payload containing sortField and sortBy.
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchPreauths({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    if (selectedEndConsumer?.userCustomId) {
      if (isFilterUpdated || preauths.length === 0) {
        const queryParameters = GetParametersFromCommonFilters(filters);
        fetchPreauths({
          page: DefaultApiLimit.page,
          limit,
          ...queryParameters,
        });
        dispatch(disableConsumerPaymentHistoryFilterApplied());
      }
      fetchStatusCounts();
    }
  }, [filters, selectedEndConsumer?.userCustomId]);

  useEffect(() => {
    if (searchParameters.get('sessionId')) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('sessionId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  return (
    <div className="payment-management-wrap">
      <div className="stats-bar">
        <div
          className="stat-item"
          role="presentation"
          onClick={handleTotalCardClick}>
          <span className="stat-label">{t('total', { defaultValue: 'Total' })}</span>
          <span className="stat-value">{statusCounts?.total ?? totalResults ?? 0}</span>
        </div>
        <div className="stat-divider" />
        <button
          type="button"
          className={`stat-badge reserved-badge ${isStatusCardActive('RESERVED') ? 'active' : ''}`}
          onClick={() => handleStatusCardClick('RESERVED')}>
          <span className="badge-dot" />
          {t('reserved', { defaultValue: 'Reserved' })}:{' '}
          {statusCounts?.counts?.RESERVED ?? 0}
        </button>
        <button
          type="button"
          className={`stat-badge linked-badge ${isStatusCardActive('LINKED') ? 'active' : ''}`}
          onClick={() => handleStatusCardClick('LINKED')}>
          <span className="badge-dot" />
          {t('linked', { defaultValue: 'Linked' })}:{' '}
          {statusCounts?.counts?.LINKED ?? 0}
        </button>
        <button
          type="button"
          className={`stat-badge captured-badge ${isStatusCardActive('CAPTURED') ? 'active' : ''}`}
          onClick={() => handleStatusCardClick('CAPTURED')}>
          <span className="badge-dot" />
          {t('captured', { defaultValue: 'Captured' })}:{' '}
          {statusCounts?.counts?.CAPTURED ?? 0}
        </button>
        <button
          type="button"
          className={`stat-badge released-badge ${isStatusCardActive('RELEASED') ? 'active' : ''}`}
          onClick={() => handleStatusCardClick('RELEASED')}>
          <span className="badge-dot" />
          {t('released', { defaultValue: 'Released' })}:{' '}
          {statusCounts?.counts?.RELEASED ?? 0}
        </button>
        <button
          type="button"
          className={`stat-badge orphaned-badge ${isStatusCardActive('ORPHANED') ? 'active' : ''}`}
          onClick={() => handleStatusCardClick('ORPHANED')}>
          <span className="badge-dot" />
          {t('orphaned', { defaultValue: 'Orphaned' })}:{' '}
          {statusCounts?.counts?.ORPHANED ?? 0}
        </button>
      </div>
      <div className="table-content">
        <CustomTable
          dataKey="EMSPPreauth"
          version="v2"
          header={column}
          data={preauths}
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          serialNo
          created
          lastUpdated
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          onHiddenColumnsChange={setHiddenColumns}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updateConsumerPaymentHistoryFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchConsumerPaymentHistoryFilters}
          refreshRequired
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
          handleRefresh={handleRefresh}
          refershCurrentPages={handleRefresh}
          exportDataPdfOrCSV={
            permissionCheck('mobilityTransactionsExport') ? exportDataPdfOrCSV : undefined
          }
          exportLoader={exportLoader}
          hoverLabel={t('refresh')}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          onSidePanelClose={handleSidePanelClose}
        />
      </div>
    </div>
  );
}

export default PaymentHistoryList;
